/**
 * Utility functions for content moderation
 */

// Reasons for flagging content
export const flagReasons = [
  'Hate speech or discrimination',
  'Harassment or bullying',
  'Inappropriate content',
  'Misinformation',
  'Spam',
  'Safety concern',
  'Other'
];

// Content types that can be flagged
export const contentTypes = [
  'post',
  'story',
  'review',
  'comment',
  'profile',
  'message'
];

// Mock moderation queue (in a real app, this would be in a database)
let moderationQueue = [];

/**
 * Flag content for moderation
 * @param {Object} params - Parameters for flagging
 * @param {string} params.contentType - Type of content being flagged
 * @param {string|number} params.contentId - ID of the content
 * @param {string|number} params.reporterId - ID of user reporting the content
 * @param {string} params.reason - Reason for flagging
 * @param {string} params.details - Additional details
 * @returns {Object} - Result of the operation
 */
export const flagContent = ({ contentType, contentId, reporterId, reason, details }) => {
  if (!contentType || !contentId || !reporterId || !reason) {
    return { success: false, message: 'Missing required information' };
  }

  const flagId = Date.now();
  
  const flaggedItem = {
    id: flagId,
    contentType,
    contentId,
    reporterId,
    reason,
    details: details || '',
    status: 'pending',
    timestamp: new Date().toISOString(),
    reviewedBy: null,
    reviewedAt: null,
    resolution: null
  };
  
  // Add to moderation queue
  moderationQueue.push(flaggedItem);
  
  return { 
    success: true, 
    message: 'Content has been flagged for review',
    flagId 
  };
};

/**
 * Get the moderation queue
 * @param {Object} filters - Optional filters
 * @param {string} filters.status - Filter by status (pending, reviewed, dismissed)
 * @param {string} filters.contentType - Filter by content type
 * @returns {Array} - Filtered moderation queue
 */
export const getModerationQueue = (filters = {}) => {
  let filtered = [...moderationQueue];
  
  if (filters.status) {
    filtered = filtered.filter(item => item.status === filters.status);
  }
  
  if (filters.contentType) {
    filtered = filtered.filter(item => item.contentType === filters.contentType);
  }
  
  return filtered;
};

/**
 * Review a flagged item
 * @param {Object} params - Parameters for review
 * @param {string|number} params.flagId - ID of the flag
 * @param {string|number} params.moderatorId - ID of the moderator
 * @param {string} params.resolution - Resolution (remove, warn, dismiss)
 * @param {string} params.notes - Moderator notes
 * @returns {Object} - Result of the operation
 */
export const reviewFlaggedItem = ({ flagId, moderatorId, resolution, notes }) => {
  if (!flagId || !moderatorId || !resolution) {
    return { success: false, message: 'Missing required information' };
  }
  
  const itemIndex = moderationQueue.findIndex(item => item.id === flagId);
  
  if (itemIndex === -1) {
    return { success: false, message: 'Flagged item not found' };
  }
  
  // Update the item
  moderationQueue[itemIndex] = {
    ...moderationQueue[itemIndex],
    status: 'reviewed',
    reviewedBy: moderatorId,
    reviewedAt: new Date().toISOString(),
    resolution,
    notes: notes || ''
  };
  
  return { 
    success: true, 
    message: `Item has been ${resolution}ed`
  };
};

/**
 * Block a user
 * @param {string|number} userId - ID of the user to block
 * @param {string|number} blockedBy - ID of the user doing the blocking
 * @returns {Object} - Result of the operation
 */
export const blockUser = (userId, blockedBy) => {
  if (!userId || !blockedBy) {
    return { success: false, message: 'Missing required information' };
  }
  
  // In a real app, this would update a database
  // For now, we'll just return a success message
  
  return { 
    success: true, 
    message: 'User has been blocked'
  };
};

/**
 * Check if content contains prohibited terms
 * @param {string} content - The content to check
 * @returns {boolean} - Whether the content contains prohibited terms
 */
export const containsProhibitedTerms = (content) => {
  if (!content || typeof content !== 'string') return false;
  
  // This is a very simplified implementation
  // In a real app, this would use more sophisticated content analysis
  const prohibitedTerms = [
    'slur',
    'hate',
    'offensive term'
    // In a real app, this would be a comprehensive list
  ];
  
  const contentLower = content.toLowerCase();
  
  return prohibitedTerms.some(term => contentLower.includes(term));
};

/**
 * Check if content is safe to post
 * @param {string} content - The content to check
 * @returns {Object} - Result of the check
 */
export const validateContentSafety = (content) => {
  if (containsProhibitedTerms(content)) {
    return { 
      safe: false, 
      message: 'Content contains prohibited terms that violate our community guidelines'
    };
  }
  
  return { safe: true };
};

/**
 * Get community guidelines
 * @returns {Array} - List of community guidelines
 */
export const getCommunityGuidelines = () => {
  return [
    'Be respectful and inclusive of all identities',
    'Do not share personal information of others without consent',
    'No hate speech, discrimination, or harassment',
    'Respect local laws and customs of Caribbean destinations',
    'Do not post content that could put LGBTQ+ individuals at risk',
    'Provide accurate information about safety and inclusivity',
    'Respect the privacy and anonymity choices of other users',
    'Report harmful content or safety concerns promptly'
  ];
};
